import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import {
  Autosuggest,
  Button,
  Checkbox,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  Row,
  Select,
  useOverlay,
} from '@fs/zion-ui'
import { PersonContext } from '../../PersonContext'
import { DotSeparator } from '../../../metadata/MetaData'
import usePrintCardsController from './usePrintCardsController'
import { RawData } from '../../../common/RawData'
import useTempleLanguages from '../../../../hooks/useCardsLanguages'

export default function PrintCardsButton({ effectiveOrdinances, ordinanceData }) {
  const { t } = useTranslation()
  const printDialogOpener = useOverlay()
  const { currentPerson } = useContext(PersonContext)
  const { printCardsState, printCardsActions } = usePrintCardsController(
    currentPerson,
    effectiveOrdinances,
    printDialogOpener,
    ordinanceData
  )
  const { languages } = useTempleLanguages()

  const ordinanceCheckboxes = {
    baptism: {
      index: 0,
      disabled: printCardsActions.existsOrdinanceOfType('BAPTISM') || printCardsActions.ownedByOtherUser('BAPTISM'),
      setCheckbox: printCardsActions.setCheckboxBaptism,
    },
    confirmation: {
      index: 1,
      disabled:
        printCardsActions.existsOrdinanceOfType('CONFIRMATION') || printCardsActions.ownedByOtherUser('CONFIRMATION'),
      setCheckbox: printCardsActions.setCheckboxConfirmation,
    },
    initiatory: {
      index: 2,
      disabled:
        printCardsActions.existsOrdinanceOfType('INITIATORY') || printCardsActions.ownedByOtherUser('INITIATORY'),
      setCheckbox: printCardsActions.setCheckboxInitiatory,
    },
    endowment: {
      index: 3,
      disabled: printCardsActions.existsOrdinanceOfType('ENDOWMENT') || printCardsActions.ownedByOtherUser('ENDOWMENT'),
      setCheckbox: printCardsActions.setCheckboxEndowment,
    },
  }

  const cascadeCheck = (ordinance) => {
    Object.values(ordinanceCheckboxes).forEach((checkbox) => {
      if (ordinance.index >= checkbox.index && !checkbox.disabled) {
        checkbox.setCheckbox(true)
      }
    })
  }

  const cascadeUncheck = (ordinance) => {
    Object.values(ordinanceCheckboxes).forEach((checkbox) => {
      if (ordinance.index <= checkbox.index && !checkbox.disabled) {
        checkbox.setCheckbox(false)
      }
    })
  }

  const checkOrdinance = (checked, ordinanceKey) => {
    const ordinance = ordinanceCheckboxes[ordinanceKey]
    if (!ordinance.disabled) {
      if (checked) {
        cascadeCheck(ordinance)
      } else {
        cascadeUncheck(ordinance)
      }
    }
  }

  if (!currentPerson) {
    return null
  }

  return (
    <>
      <Button
        id="printOrdinanceCardsBtn"
        data-testid="printOrdinancesButton"
        emphasis="medium"
        onClick={printDialogOpener.open}
      >
        {t('ordinances.printFoc', 'Print Family Ordinance Cards')}
      </Button>
      <DialogOverlay
        data-testid="printFamilyOrdinanceCardsDialogTestId"
        {...printDialogOpener}
        title={t('ordinances.printFoc', 'Print Family Ordinance Cards')}
      >
        <Row gutters="xxs">
          {currentPerson.name}
          <DotSeparator />
          {currentPerson.id}
          <DotSeparator />
          {currentPerson.lifespan}
        </Row>
        <Row>
          <>
            <Checkbox
              name="checkBaptism"
              label={t('ordinances.types.baptism', 'Baptism')}
              checked={printCardsState.checkboxBaptism}
              onChange={(event) => checkOrdinance(event.target.checked, 'baptism')}
              disabled={ordinanceCheckboxes.baptism.disabled}
            />
            <Checkbox
              name="checkConfirmation"
              label={t('ordinances.types.confirmation', 'Confirmation')}
              checked={printCardsState.checkboxConfirmation}
              onChange={(event) => checkOrdinance(event.target.checked, 'confirmation')}
              disabled={ordinanceCheckboxes.confirmation.disabled}
            />
            <Checkbox
              name="checkInitiatory"
              label={t('ordinances.types.initiatory', 'Initiatory')}
              checked={printCardsState.checkboxInitiatory}
              onChange={(event) => checkOrdinance(event.target.checked, 'initiatory')}
              disabled={ordinanceCheckboxes.initiatory.disabled}
            />
            <Checkbox
              name="checkEndowment"
              label={t('ordinances.types.endowment', 'Endowment')}
              checked={printCardsState.checkboxEndowment}
              onChange={(event) => checkOrdinance(event.target.checked, 'endowment')}
              disabled={ordinanceCheckboxes.endowment.disabled}
            />
            <Checkbox
              name="checkSP"
              label={t('ordinances.types.sealingToParents', 'Sealing to Parents')}
              checked={printCardsState.checkboxSealingToParents}
              onChange={(event) => printCardsActions.setCheckboxSealingToParents(event.target.checked)}
              disabled={
                !currentPerson.relationships.parents ||
                currentPerson.relationships.parents.length < 1 ||
                printCardsActions.ownedByOtherUser('SEALING_TO_PARENTS')
              }
            />
            {printCardsState.checkboxSealingToParents && (
              <ParentsSelect
                value={printCardsState.selectedParents}
                onChange={printCardsActions.setSelectedParents}
                relationshipList={currentPerson.relationships.parents}
              />
            )}
            <Checkbox
              name="checkSS"
              label={t('ordinances.types.sealingToSpouse', 'Sealing to Spouse')}
              checked={printCardsState.checkboxSealingToSpouse}
              onChange={(event) => printCardsActions.setCheckboxSealingToSpouse(event.target.checked)}
              disabled={
                !currentPerson.relationships.spouses ||
                currentPerson.relationships.spouses.length < 1 ||
                printCardsActions.ownedByOtherUser('SEALING_TO_SPOUSE')
              }
            />
            {printCardsState.checkboxSealingToSpouse && (
              <SpouseSelect
                value={printCardsState.selectedSpouse}
                onChange={printCardsActions.setSelectedSpouse}
                relationshipList={currentPerson.relationships.spouses}
              />
            )}
          </>
          <>
            <Select
              label={t('language.select', 'Select Language')}
              aria-label={t('language.select', 'Select Language')}
              value={printCardsState.selectedLang}
              onChange={(event) => printCardsActions.setSelectedLang(event.target.value)}
              options={languages.map((lang) => {
                return { value: lang.code, label: `${lang.code} - ${lang.name}` }
              })}
            />
            <Checkbox
              name="checkContactInfo"
              label={t('ordinances.options.excludeContactInfo', 'Exclude Contact Information')}
              checked={printCardsState.checkboxExcludeContactInfo}
              onChange={(event) => printCardsActions.setCheckboxExcludeContactInfo(event.target.checked)}
            />
          </>
        </Row>

        <RawData state={printCardsState} />

        <DialogOverlayFooter>
          <DialogOverlayActions>
            <Button
              id="printBtn"
              emphasis="high"
              disabled={!printCardsActions.isGoodToPrint()}
              onClick={printCardsActions.print}
            >
              {t('action.print', 'Print')}
            </Button>
            <Button emphasis="medium" onClick={printCardsActions.handleClose}>
              {t('action.cancel', 'Cancel')}
            </Button>
          </DialogOverlayActions>
        </DialogOverlayFooter>
      </DialogOverlay>
    </>
  )
}

function RelationshipSelect({ relationshipType, value, onChange, relationshipList, disabled }) {
  const { t } = useTranslation()
  const labelType = relationshipType.charAt(0).toUpperCase() + relationshipType.slice(1)
  return (
    <Autosuggest
      aria-label="relationshipSelect"
      label={t(`ordinances.${relationshipType}ToBeSealedTo`, `${labelType} to be sealed to`)}
      selectedItem={value}
      disabled={disabled}
      onSuggestionSelected={(newSuggestion) => newSuggestion && onChange(newSuggestion)}
      suggestions={
        relationshipList &&
        relationshipList.map((relationship) => {
          let label = ''
          if (relationshipType === 'spouse') {
            label = `${relationship.spouse.name} (${relationship.spouse.personId})`
          } else if (relationshipType === 'parents') {
            label = `${relationship.parent1.name} (${relationship.parent1.personId}) and ${relationship.parent2.name} (${relationship.parent2.personId})`
          }
          return { key: relationship.relationshipId, primaryText: label, relationship }
        })
      }
    />
  )
}

RelationshipSelect.propTypes = {
  relationshipType: PropTypes.oneOf(['spouse', 'parents']).isRequired,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  onChange: PropTypes.func,
  relationshipList: PropTypes.arrayOf(
    PropTypes.shape({
      relationshipId: PropTypes.string,
    })
  ),
}

export function SpouseSelect({ value, onChange, relationshipList, disabled }) {
  return (
    <RelationshipSelect
      disabled={disabled}
      relationshipType="spouse"
      value={value}
      onChange={onChange}
      relationshipList={relationshipList}
    />
  )
}

export function ParentsSelect({ value, onChange, relationshipList, disabled }) {
  return (
    <RelationshipSelect
      disabled={disabled}
      relationshipType="parents"
      value={value}
      onChange={onChange}
      relationshipList={relationshipList}
    />
  )
}
